import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DiscussionApiService } from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';
import { FormatService } from '../../../../shared/services/format.service';
import {
  DISCUSSION_CATEGORY_SIDEBAR_TABS,
  discussionTypeToDisplayLabel,
  discussionTypeToSidebarTab,
} from '../discussion-type.util';
// Share feature: commented out for now. Uncomment to re-enable.
// import { CardShareButtonComponent } from '../../../../shared/components/card-share-button/card-share-button.component';

export interface DiscussionItem {
  id: string;
  title: string;
  description: string;
  content?: string;
  author: string;
  authorAvatar?: string;
  category: string;
  /** Discussion channel/type from API (e.g. GETTING_STARTED, PUBLIC) — shown as badge beside category chip */
  discussionType?: string;
  replies: number;
  views: number;
  lastActivity: string;
  /** Formatted date for display (e.g. "Feb 11, 2026") */
  formattedDate?: string;
  /** Status label for badge (e.g. "Approved", "Pending", "Rejected") */
  statusLabel?: string;
  /** Raw status for styling (APPROVED, PENDING, REJECTED, CHANGES_REQUIRED) */
  status?: string;
  isPinned?: boolean;
  isBookmarked?: boolean;
  isVoted?: boolean;
  assetType?: 'Dataset' | 'Model' | 'Usecase';
  assetName?: string;
  tags?: string[];
  attachments?: any[];
  /** Raw API created-at (ISO) for “Discussion created at” tooltip on cards only */
  createdAt?: string;
}

@Component({
  selector: 'app-discussion-list',
  standalone: true,
  imports: [CommonModule /* , CardShareButtonComponent */], // Share feature: uncomment to re-enable
  templateUrl: './discussion-list.component.html',
  styleUrl: './discussion-list.component.scss',
})
export class DiscussionListComponent {
  @Input() discussions: DiscussionItem[] = [];
  @Input() isLoggedIn: boolean = false;
  @Input() categoryName: string = ''; // Category name for empty state message
  /** When true, show delete button (for owner's discussions, e.g. My Discussions page) */
  @Input() showDeleteForOwner: boolean = false;
  /** When true, show edit button only for CHANGES_REQUIRED or PENDING status (e.g. Post Submission page) */
  @Input() showEditOnlyForChangesRequired: boolean = false;
  /** When true, hide status badge on cards (e.g. when tab already indicates status) */
  @Input() hideStatusBadge: boolean = false;
  @Output() bookmarkClick = new EventEmitter<{ event: Event; discussion: DiscussionItem }>();
  @Output() pinClick = new EventEmitter<{ event: Event; discussion: DiscussionItem }>();
  @Output() editClick = new EventEmitter<{ event: Event; discussion: DiscussionItem }>();
  @Output() deleteClick = new EventEmitter<{ event: Event; discussion: DiscussionItem }>();

  constructor(
    private router: Router,
    private discussionApiService: DiscussionApiService,
    private keycloakService: KeycloakService,
    private formatService: FormatService
  ) {}

  /** Infer current discussion tab from route so detail page can preserve sidebar highlight. */
  private getCurrentTabFromUrl(): string | null {
    const [path, query] = this.router.url.split('?');
    const segments = path.split('/').filter(Boolean);
    const discussionsTab = segments[0] === 'discussions' ? segments[1] : null;

    if (discussionsTab && DISCUSSION_CATEGORY_SIDEBAR_TABS.has(discussionsTab)) {
      return discussionsTab;
    }

    if (query) {
      const tab = new URLSearchParams(query).get('tab');
      if (tab && DISCUSSION_CATEGORY_SIDEBAR_TABS.has(tab)) {
        return tab;
      }
    }

    return null;
  }

  private resolveSidebarTabForNavigation(discussion?: DiscussionItem): string | null {
    const tabFromType = discussionTypeToSidebarTab(discussion?.discussionType);
    if (tabFromType) return tabFromType;
    return this.getCurrentTabFromUrl();
  }

  navigateToDiscussion(discussionId: string, discussion?: DiscussionItem): void {
    const tab = this.resolveSidebarTabForNavigation(discussion);
    const navigationExtras = tab ? { queryParams: { tab } } : {};
    this.discussionApiService.getReview(discussionId).then(() => {
      this.router.navigate(['/discussions', discussionId], navigationExtras);
    }).catch(() => {
      // Still navigate on error so user can reach the detail page
      this.router.navigate(['/discussions', discussionId], navigationExtras);
    });
  }

  onBookmarkClick(event: Event, discussion: DiscussionItem): void {
    event.stopPropagation();
    this.bookmarkClick.emit({ event, discussion });
  }

  onPinClick(event: Event, discussion: DiscussionItem): void {
    event.stopPropagation();
    this.pinClick.emit({ event, discussion });
  }

  onEditClick(event: Event, discussion: DiscussionItem): void {
    event.stopPropagation();
    this.editClick.emit({ event, discussion });
  }

  onDeleteClick(event: Event, discussion: DiscussionItem): void {
    event.stopPropagation();
    this.deleteClick.emit({ event, discussion });
  }

  /** Forum channel label for badge text (e.g. Getting Started, Feedback). */
  getDiscussionTypeDisplayLabel(value: string): string {
    return discussionTypeToDisplayLabel(value);
  }

  /** Map API category to display label. */
  getCategoryDisplayLabel(category: string): string {
    if (!category) return '';
    const normalized = category.replace(/[\s_-]/g, '').toUpperCase();
    if (normalized === 'DATABANKS' || normalized.includes('DATABANK') || normalized === 'DATA_BANKS') return 'Datasets';
    if (normalized === 'AIMODELS' || normalized === 'AIMODEL' || normalized === 'AI_MODELS' || normalized === 'AI_MODEL') return 'Models';
    if (normalized === 'USECASES' || normalized === 'USECASE' || normalized === 'APPS' || normalized === 'APP') return 'Use Case';
    return category;
  }

  formatRelativeTime(dateString: string): string {
    return this.formatService.formatDiscussionActivity(dateString);
  }

  /** Show clock + tooltip only when API sent a created timestamp */
  hasCreatedAtForTooltip(d: DiscussionItem): boolean {
    return !!(d.createdAt?.trim());
  }

  /** Tooltip copy: label + datetime in IST (same rules as FormatService.formatDate) */
  getDiscussionCreatedTooltip(d: DiscussionItem): string {
    const raw = (d.createdAt ?? '').trim();
    if (!raw) return '';
    const when = this.formatService.formatDate(raw);
    return when ? `Discussion created at: ${when}` : 'Discussion created at';
  }
}
